import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { FaGift } from "react-icons/fa";
import { WhatsNewRelease } from "../../data/whatsNew";

interface WhatsNewDialogProps {
  releases: WhatsNewRelease[];
  /** True when the dialog opened by itself because there was something unread. */
  openedAutomatically: boolean;
  onClose: () => void;
}

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);

  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * What has changed in the portal, opened from the gift icon beside the user's
 * name and shown once on its own after an update.
 *
 * The notes come from src/data/whatsNew.ts, which ships with the build.
 */
const WhatsNewDialog = ({
  releases,
  openedAutomatically,
  onClose,
}: WhatsNewDialogProps) => {
  return (
    <Dialog
      title="What's New"
      onClose={onClose}
      /* Sized against the viewport as well as in pixels, so the dialog is the
         same shape on a desktop but still fits a laptop or a tablet. */
      width="min(560px, 92vw)"
    >
      <div style={{ padding: "1rem" }}>
        {/* Said first and every time, so nobody has to go looking for the notes
            they just dismissed. */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.6rem",
            padding: "0.75rem",
            marginBottom: "1.25rem",
            borderRadius: "6px",
            background: "#f3f7ff",
            border: "1px solid #d6e4ff",
          }}
        >
          <FaGift style={{ color: "#2563eb", marginTop: "2px", flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: "12px", color: "#323130" }}>
            {openedAutomatically
              ? "This opens once after each update. "
              : ""}
            You can read these notes again whenever you like — the{" "}
            <strong>What&apos;s New</strong> button sits next to your name in the
            top right.
          </p>
        </div>

        {/* Fixed height rather than one that grows with the notes: the dialog
            stays the same size whether there is one release to read or twenty,
            and a long history scrolls instead of running off the screen. The
            callout above stays put while it does. */}
        <div
          style={{
            height: "min(360px, 50vh)",
            overflowY: "auto",
            paddingRight: "0.5rem",
          }}
        >
          {releases.length === 0 ? (
            <p style={{ margin: 0, fontSize: "13px", color: "#605e5c" }}>
              There is nothing new to report just yet.
            </p>
          ) : (
            releases.map((release) => (
              <section key={release.version} style={{ marginBottom: "1.5rem" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: "1rem",
                    paddingBottom: "0.4rem",
                    borderBottom: "1px solid #edebe9",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#323130",
                    }}
                  >
                    {formatDate(release.date)}
                  </h3>
                  <span style={{ fontSize: "11px", color: "#a19f9d" }}>
                    Version {release.version}
                  </span>
                </div>

                {release.items.map((item) => (
                  <div key={item.title} style={{ marginTop: "0.85rem" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#323130",
                      }}
                    >
                      {item.title}
                    </div>
                    <p
                      style={{
                        margin: "0.2rem 0 0",
                        fontSize: "12px",
                        lineHeight: 1.5,
                        color: "#605e5c",
                      }}
                    >
                      {item.description}
                    </p>
                  </div>
                ))}
              </section>
            ))
          )}
        </div>
      </div>

      <DialogActionsBar>
        <Button themeColor="primary" onClick={onClose}>
          Got it
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default WhatsNewDialog;
